import React, { useEffect, useState, useCallback } from 'react';
import CloseIcon from '@material-ui/icons/Close';
import { CircularProgress, Tooltip, Button } from '@material-ui/core';
import { fetchGptText } from '../../utility/cr-common';
import { useQalert } from '../../utility/services/customHooks';
import { useDispatch, useSelector } from 'react-redux';
import { actionTypes } from '../../constants/actionTypes';
import { copyUrlToClipboard } from '../../utility/common';
import { FilterNone } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core';
import QuickBriefGenerator from './QuickBriefGenerator';

const useStyles = makeStyles({
  tooltipStyle: {
    zIndex: 9999,
  },
});

const AutoWriteEditor = () => {
  const classes = useStyles();
  const [_, toggleSnackBar] = useQalert();
  const [isOpen, setIsOpen] = useState(false);
  const [showQuickBriefForm, setshowQuickBriefForm] = useState(false);
  const [autoWriteLoader, setAutoWriteLoader] = useState(false);
  const [textValue, setTextValue] = useState('');
  const [titleForCopyAction, setTitleForCopyAction] = React.useState('Copy');

  const dispatch = useDispatch();

  const globalDetailsAvailable: {
    open: boolean;
    defaultText: string;
  } = useSelector((state) => state.root.globalAutoWriterMagicPin);

  useEffect(() => {
    if (globalDetailsAvailable?.open) {
      setIsOpen(true);
      setTextValue(globalDetailsAvailable?.defaultText || '');
    }
  }, [globalDetailsAvailable]);

  useEffect(() => {
    console.log('final textValue', JSON.stringify(textValue));
  }, [textValue]);

  const autoWriteOpenCloseHandler = useCallback(() => {
    const newOpenValue = !isOpen;
    setIsOpen(newOpenValue);
    if (!newOpenValue) {
      setTextValue('');
      if (globalDetailsAvailable?.open || globalDetailsAvailable?.defaultText) {
        dispatch({
          type: actionTypes.GLOBAL_AUTO_WRITER_MAGIC_PIN,
          payload: {
            open: false,
            defaultText: '',
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, textValue, globalDetailsAvailable]);

  const autoWriteHandler = async () => {
    try {
      if (textValue && textValue.trim()) {
        setAutoWriteLoader(true);
        let text = textValue;
        const newText = await fetchGptText(text, null, 500);
        if (newText) {
          const gptTextEffect = `<span class="gpt-text-appended">${newText}</span>`;
          text = text?.replace(/(<([^>]+)>)/gi, '');
          setTextValue(text + gptTextEffect);
        } else {
          toggleSnackBar({
            type: 'warning',
            text: 'Auto-write offered no suggestions for this prompt. You may try again with some changes to the text entered.',
            open: true,
          });
        }
      } else {
        toggleSnackBar({
          type: 'warning',
          text: `Text can't be blank for Auto Write`,
          open: true,
        });
      }
    } catch (error) {
      toggleSnackBar({ type: 'error', text: 'Oops! Something Went Wrong. Please try again.', open: true });
      console.error('AutoWriteEditor:: autoWriteHandler:: error', error);
    } finally {
      setAutoWriteLoader(false);
    }
  };

  const copyHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    copyUrlToClipboard(textValue?.replace(/(<([^>]+)>)/gi, ''));
    setTitleForCopyAction('Copied');
    setTimeout(() => {
      setTitleForCopyAction('Copy');
    }, 1000);
  };

  return (
    <div data-testid="test-auto-write-editor-wrapper" className="auto-write-editor-wrapper">
      {isOpen ? (
        <div data-testid="test-editor-wrapper" className="editor-wrapper">
          {showQuickBriefForm ? (
            <>
              <QuickBriefGenerator resetQuickBrief={() => setshowQuickBriefForm(false)} />
            </>
          ) : (
            <>
              <div data-testid="test-title-wrapper" className="title-wrapper">
                <p className="m-0 title">Auto-Write Content</p>
              </div>
              <div
                data-testid="test-editor"
                data-placeholder="...Type a command and hit auto-write to generate content."
                className="m-0 editor pre-wrap"
                onBlur={(e) => {
                  const newtext = e?.currentTarget?.innerHTML
                    ?.replace(/<br>/gi, '\n')
                    .replace('<div>', '\n')
                    .replace(/<[^>]*>?/gm, '');
                  setTextValue(newtext || '');
                }}
                contentEditable={true}
                dangerouslySetInnerHTML={{ __html: textValue }}
              />
              <div data-testid="test-action-wrapper" className="action-wrapper">
                {/* eslint-disable-next-line  */}
                <Tooltip title={titleForCopyAction} classes={{ popper: classes?.tooltipStyle }} placement="top" arrow>
                  <FilterNone data-testid="test-action-copy" className="copy-icon" onClick={(e) => copyHandler(e)} />
                </Tooltip>
                <p
                  data-testid="test-action-auto-write"
                  className={`m-0 auto-write ${autoWriteLoader ? 'disabled' : ''}`}
                  onClick={autoWriteHandler}
                >
                  {autoWriteLoader ? <CircularProgress className="position-abs" size={20} /> : null}
                  Auto-Write
                </p>
              </div>
            </>
          )}
          <div className={`additional-options ${!showQuickBriefForm ? 'm-tb-16' : ''}`}>
            {showQuickBriefForm ? (
              <></>
            ) : (
              <>
                <p className="m-0 title">Additional Options</p>
                <Button variant="outlined" onClick={() => setshowQuickBriefForm(!showQuickBriefForm)}>
                  Generate Quick Brief
                </Button>
              </>
            )}
          </div>
        </div>
      ) : null}

      {/* eslint-disable-next-line  */}
      <div
        data-testid="test-floating-icon"
        className={`floating-icon ${autoWriteLoader ? 'disabled' : ''}`}
        onClick={autoWriteOpenCloseHandler}
      >
        {!isOpen ? (
          <svg
            data-testid="test-magic-icon"
            className="magic-icon MuiSvgIcon-root MuiSvgIcon-fontSizeMedium MuiBox-root css-1om0hkc"
            focusable="false"
            aria-hidden="true"
            viewBox="0 0 24 24"
          >
            <path d="M7.5 5.6 10 7 8.6 4.5 10 2 7.5 3.4 5 2l1.4 2.5L5 7zm12 9.8L17 14l1.4 2.5L17 19l2.5-1.4L22 19l-1.4-2.5L22 14zM22 2l-2.5 1.4L17 2l1.4 2.5L17 7l2.5-1.4L22 7l-1.4-2.5zm-7.63 5.29a.9959.9959 0 0 0-1.41 0L1.29 18.96c-.39.39-.39 1.02 0 1.41l2.34 2.34c.39.39 1.02.39 1.41 0L16.7 11.05c.39-.39.39-1.02 0-1.41l-2.33-2.35zm-1.03 5.49-2.12-2.12 2.44-2.44 2.12 2.12-2.44 2.44z"></path>
          </svg>
        ) : (
          <CloseIcon data-testid="test-close-icon" className="magic-icon" />
        )}
      </div>
    </div>
  );
};

export default React.memo(AutoWriteEditor);
